import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  CardSeriesFormRule,
  CardSeriesFormSchema,
} from "@factory/form/form.rule";
import { useFormDefaultCardSeries } from "@factory/form/form-default-card-series.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import {
  GetCardSeriesExtendDto,
  GetResponseCardSeriesModel,
} from "@factory/model/query-model-card-series";
import {
  useQyActivateCardSeries,
  useQyDeleteCardSeries,
  useQyGetCardSeriesById,
} from "@factory/query/card-series.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditCardSeries() {
  // LOCAL DECLARATION
  const { getDefault } = useFormDefaultCardSeries();
  const navigate = useNavigate();
  const { mapUpdateCardSeries, mapDeleteCardSeries } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { cardSeriesId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
  ];

  // QUERY DECLARATION
  // API GETBYID CardSeries
  const handleGetApiSuccess = (data: GetResponseCardSeriesModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("tenant", responseData?.tenant || "");
    setValue("cardCategory", responseData?.card_category || "");
  };
  const {
    data: cardSeriesResponse,
    isLoading,
    isError: cardSeriesError,
  } = useQyGetCardSeriesById(cardSeriesId || "", handleGetApiSuccess);
  const cardSeriesData = cardSeriesResponse?.data?.[0];

  // API UPDATE CardSeries
  const handleUpdateApiSuccess = () => {
    showSuccess("CardSeries updated successfully");
  };
  const { mutate: updateCardSeries, isLoading: isEditLoading } =
    useQyActivateCardSeries(handleUpdateApiSuccess);

  // API DELETE CardSeries
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("CardSeries deleted successfully");
  };
  const { mutate: deleteCardSeries } = useQyDeleteCardSeries(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<CardSeriesFormSchema>({
    defaultValues: getDefault(cardSeriesData as GetCardSeriesExtendDto),
    resolver: zodResolver(CardSeriesFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: CardSeriesFormSchema) => {
    if (!cardSeriesData || !cardSeriesData.code) {
      throw new Error("No cardSeries data");
    }

    const formData = mapUpdateCardSeries(form, cardSeriesData.code);
    updateCardSeries(formData);
  };
  const handleValidateError = (err: FieldErrors<CardSeriesFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!cardSeriesData || !cardSeriesData.code) {
      throw new Error("No cardSeries data found");
    }

    const form = mapDeleteCardSeries(cardSeriesData.code);
    deleteCardSeries(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    cardSeriesId,
    cardSeriesData,
    isLoading,
    cardSeriesError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
